import React from 'react';
import { useIntl } from 'react-intl';

import { TPermissionsCtxValue } from '@/app/contexts/permissions';
import { IDriver } from '@/modules/driver/model/driver';
import { IPartner } from '@/modules/partner/model';
import { TCountryCodes, CountryCode } from '@/shared/models/country-code';
import { ChangeEmailTip } from '@/shared/view/ChangeEmailTip';
import { StepLayout } from '@/shared/view/StepLayout';
import { DocsList, IDocsListItem } from '@src-common/view/components/DocsList';

import { useAcademyCard } from '../Todo/hooks/useAcademyCards';
import { ToDoView } from '../Todo/view';

import { AgencyAgreementConfirmation } from './AgencyAgreementConfirmation';
import { ChauffeurInfo } from './ChauffeurInfo';
import { isCompany, isSelfEmployed } from './company';
import { CompanyInfo } from './CompanyInfo';
import { ContactInfo } from './ContactInfo';
import { ContractDetails } from './ContractDetails';
import { PaymentInfo } from './PaymentInfo';
import { SelfEmployedInfo } from './SelfEmployedTaxInfo';
import { Settings } from './Settings';
import { StatusInfo } from './StatusInfo';
import s from './styles.module.css';

interface IProps<T extends IDocsListItem> {
  user: TPermissionsCtxValue['user'];
  region: TCountryCodes;
  partner: IPartner;
  docs: T[];
  onUploadDoc: (event: React.SyntheticEvent, document: T) => void;
  driver: IDriver;
  onClickEditPayment: () => void;
  onClickEditPayouts: (params?: { isCompany?: boolean }) => void;
  onClickEditDriver: () => void;
  onClickEditContactInformation: () => void;
  onClickBack: () => void;
  onClickAgencyAgreementLink: () => void;
  isAgencyAgreementLoading?: boolean;
}

const ProfileView = <T extends IDocsListItem>({
  region,
  user,
  partner,
  driver,
  docs,
  onUploadDoc,
  onClickEditPayment,
  onClickEditPayouts,
  onClickEditDriver,
  onClickEditContactInformation,
  onClickBack,
  onClickAgencyAgreementLink,
  isAgencyAgreementLoading,
}: IProps<T>) => {
  const intl = useIntl();
  const operator = user.isChauffeur ? driver.chauffeurOperator : partner.partnerOperator;
  const todoCards = useAcademyCard(
    {
      myDriver: driver,
    },
    intl,
  );

  const userHasPartnerRole =
    user.isFleet || user.isFleetAndChauffeur || user.isIndividualChauffeurRU;

  return (
    <StepLayout
      title={intl.formatMessage({
        defaultMessage: 'Profile',
        id: 'HCCkSJ',
        description: 'profile screen: title',
      })}
      onClickBack={onClickBack}
      hideProgress={true}
    >
      {user.isFleet ? (
        <ContactInfo
          partner={partner}
          onClickEdit={onClickEditContactInformation}
          className={s.values}
        >
          <ChangeEmailTip />
        </ContactInfo>
      ) : (
        <ChauffeurInfo
          partner={partner}
          driver={driver}
          className={s.values}
          onClickEdit={onClickEditDriver}
        >
          <ChangeEmailTip />
        </ChauffeurInfo>
      )}

      {!user.isFleet && <StatusInfo partner={partner} driver={driver} />}
      {!user.isChauffeur && isCompany(partner) && (
        <CompanyInfo
          className={s.values}
          partner={partner}
          onEdit={() => onClickEditPayouts({ isCompany: true })}
        />
      )}
      {!user.isChauffeur &&
        (user.isFleet || user.isFleetAndChauffeur || user.isIndividualChauffeur) &&
        isSelfEmployed(partner) && (
          <SelfEmployedInfo className={s.values} partner={partner} onEdit={onClickEditPayouts} />
        )}
      {operator && region === CountryCode.GreatBritain && (
        <ContractDetails className={s.values} operator={operator} />
      )}
      {!user.isChauffeur && (
        <PaymentInfo className={s.values} partner={partner} onClickEdit={onClickEditPayment} />
      )}
      {!user.isFleet && (
        <div className={s.todo}>
          <ToDoView cards={todoCards} />
        </div>
      )}
      {!user.isChauffeur && docs.length > 0 && (
        <DocsList<T>
          title={intl.formatMessage({
            defaultMessage: 'DOCUMENTS',
            id: 'wVBktP',
            description: 'Docs list title',
          })}
          className={s.docs}
          onChange={onUploadDoc}
          docs={docs}
        />
      )}
      {/* The backend serves the document to active partners only, anyone else gets a 403. */}
      {user.isFleet && region === CountryCode.Russia && partner.accessStatus === 'active' && (
        <AgencyAgreementConfirmation
          onClick={onClickAgencyAgreementLink}
          isLoading={isAgencyAgreementLoading}
          className={s.settings}
        />
      )}
      {userHasPartnerRole && <Settings partner={partner} />}
    </StepLayout>
  );
};

export { ProfileView };
